import React, { useMemo, useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import StandardModal from "../../../components/modales/StandardModal";
import { EmptyState, StatCard, type DataTableColumn } from "@routes-plan/ui";
import axios from "axios";
import moment from "moment-timezone";
import { formatCurrency } from "../../../utils/format/currency";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import { useT } from "../../../i18n";
import { ModalTable, ReportStats } from '../components/ReportLayout';
interface LoanRefinanceModalProps {
  open: boolean;
  onClose: () => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  clientLoan?: {
    cashBoxId: number;
    startDate: string;
    endDate: string;
    routeName: string;
    routeId: string;
  } | null;
}


const ConsolidatedRefinancingModal: React.FC<LoanRefinanceModalProps> = ({
  open,
  onClose,
  clientLoan,
  showAlert,
}) => {
  const t = useT();
  const [movement, setMovement] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const showAlertRef = useRef(showAlert);
  showAlertRef.current = showAlert;

  const loanKey = useMemo(() => {
    if (!clientLoan) return "";
    return [
      clientLoan.cashBoxId,
      moment(clientLoan.startDate).format("YYYY-MM-DD"),
      moment(clientLoan.endDate).format("YYYY-MM-DD"),
      String(clientLoan.routeId),
    ].join("|");
  }, [
    clientLoan?.cashBoxId,
    clientLoan?.startDate,
    clientLoan?.endDate,
    clientLoan?.routeId,
  ]);

  useEffect(() => {
    if (!open) {
      setMovement([]);
      setIsFetching(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !clientLoan || !loanKey) return;

    const ac = new AbortController();
    setIsFetching(true);
    setMovement([]);

    const fetchData = async () => {
      try {
        const formData = {
          cashBoxId: clientLoan.cashBoxId,
          startDate: moment(clientLoan.startDate).format("YYYY-MM-DD"),
          endDate: moment(clientLoan.endDate).format("YYYY-MM-DD"),
          routeId: clientLoan.routeId,
        };
        const { data } = await axios.post("/reports/consolidated-day/refinancing", formData, {
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;

        const records = Array.isArray(data?.data) ? data.data : [];
        const filtered = records.filter(Boolean);
        setMovement(filtered);
        if (filtered.length === 0) {
          showAlertRef.current(t("summaryReports.noRecordsForQuery"), "info");
        } else {
          showAlertRef.current(
            filtered.length === 1
            ? t("summaryReports.recordFoundOne")
            : t("summaryReports.recordsFound", { count: filtered.length }),
            "success"
          );
        }
      } catch (error: unknown) {
        if (ac.signal.aborted) return;
        console.error("Error al cargar datos del modal:", error);
        showAlertRef.current(t("summaryReports.consolidated.refinancedLoadError"), "error");
        setMovement([]);
      } finally {
        if (!ac.signal.aborted) {
          setIsFetching(false);
        }
      }
    };

    fetchData();

    return () => {
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loanKey]);

  const rows = useMemo(
    () =>
      (Array.isArray(movement) ? movement : []).map((data, index) => ({
        id: data?.id ?? `refi-${index}`,
        client: String(data?.client?.fullName || ""),
        interestRate: Number(data?.interestRate) || 0,
        value: Number(data?.initialValue) || 0,
        balance: Number(data?.balance) || 0,
        refinanceValue: Number(data?.value) || 0,
      })),
    [movement],
  );

  const totalValue = rows.reduce((sum, item) => sum + item.value, 0);
  const totalInterest = rows.reduce((sum, item) => sum + item.interestRate, 0);
  const totalRefinance = rows.reduce((sum, item) => sum + item.refinanceValue, 0);
  const totalBalance = rows.reduce((sum, item) => sum + item.balance, 0);

  type RefiRow = (typeof rows)[number];
  const money = (field: "interestRate" | "value" | "balance" | "refinanceValue", header: string): DataTableColumn<RefiRow> => ({
    id: field,
    header,
    align: "right",
    sortable: true,
    value: (r) => r[field],
    render: (r) => formatCurrency(r[field]),
    csv: (r) => formatCurrency(r[field]),
  });
  const columns: DataTableColumn<RefiRow>[] = [
    { id: "id", header: t("summaryReports.col.loan"), value: (r) => r.id, sortable: true },
    { id: "client", header: t("summaryReports.col.client"), value: (r) => r.client, sortable: true },
    money("interestRate", t("summaryReports.col.utility")),
    money("value", t("summaryReports.col.loanValue")),
    money("balance", t("summaryReports.col.balance")),
    money("refinanceValue", t("summaryReports.col.refinancedValue")),
  ];

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={t("summaryReports.consolidated.routeHeading", { name: clientLoan?.routeName || t("summaryReports.shared.route") })}
      subtitle={t("summaryReports.dateRange", { start: clientLoan?.startDate ?? "", end: clientLoan?.endDate ?? "" })}
      maxWidth={1000}
      loading={isFetching}
      actions={
        <>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
        </>
      }
    >
      {rows.length > 0 ? (
        <Box sx={{ display: "grid", gap: 2, minWidth: 0 }}>
          <ReportStats>
            <StatCard label={t("summaryReports.col.utility")} value={formatCurrency(totalInterest)} />
            <StatCard label={t("summaryReports.col.loanValue")} value={formatCurrency(totalValue)} />
            <StatCard label={t("summaryReports.col.balance")} value={formatCurrency(totalBalance)} />
            <StatCard label={t("summaryReports.col.refinancedValue")} value={formatCurrency(totalRefinance)} />
          </ReportStats>
          <ModalTable
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            exportFileName={t("summaryReports.consolidated.modalExportFile", { date: nowOperationalDateString() })}
            ariaLabel={t("summaryReports.consolidated.refinancedTitle")}
          />
        </Box>
      ) : isFetching ? null : (
        <EmptyState dense title={t("summaryReports.noRecordsForQuery")} />
      )}
    </StandardModal>
  );
};

export default ConsolidatedRefinancingModal;
